import React, { useEffect, useMemo, useState, useContext, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { t } from 'i18next';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid2 from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { LoadingButton } from '@mui/lab';
import { zodResolver } from '@hookform/resolvers/zod';
import zod from 'zod';

import DeviceDirectConnectionStreamComponent from '../../components/DeviceDirectConnectionStreamComponent';
import DeviceReverseConnectionStreamComponent from '../../components/DeviceReverseConnectionStreamComponent';
import DropdownSelectComponent from '../../components/DropdownSelectComponent';
import TextFieldComponent from '../../components/TextFieldComponent';
import ModalComponent from '../../components/ModalComponent';

import { getToken } from '../../services/DefaultService';
import {
  useGetAllDevicesByLicenseAsync,
  useOpenDoorAsync,
} from '../../services/Devices/hooks/useMutation';
import { IDeviceSimpleProps } from '../../services/Devices/types';

import { AppContext } from 'context/AppContextProvider';

import {
  useCreateDeviceGroupAsync,
  useDeleteDeviceGroupAsync,
  useGetAllDeviceGroupByLicenseAsync,
  useGetDeviceGroupByIdAsync,
  useUpdateDeviceGroupAsync,
} from '../../services/DeviceGroupService/hooks/useMutation';

import { DeviceGroupFormSchema } from '../../services/DeviceGroupService/schemas';
import {
  IDeviceGroupProps,
  IDeviceGroupSimpleProps,
} from '../../services/DeviceGroupService/types';
import { ClockIcon } from '@mui/x-date-pickers';
import { Collapse, ListItemButton } from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import Chip from '@mui/material/Chip';
import { RightArrowIcon } from '@react-pdf-viewer/bookmark';
import {
  DoorSliding,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LockOpenOutlined,
  LockOpenRounded,
} from '@mui/icons-material';
import CameraIcon from '@mui/icons-material/Camera';
import RegisterOpenDoorModal from './_components/RegisterOpenDoorModal';
import HistoryIcon from '@mui/icons-material/History';
import OpenDoorEventsModal from './_components/OpenDoorEventsModal';

type FormValues = {
  deviceId: number[];
};

interface DeviceMonitoringScreenProps {
  isDirectConnection?: boolean;
  initialDeviceId?: number;
}

/* ---------------------------
   MODAL: Create/Edit Device Group
---------------------------- */
type DeviceGroupFormInput = zod.infer<typeof DeviceGroupFormSchema>;

interface DeviceGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  devicesOptions: IDeviceSimpleProps[];
  groupToEdit?: IDeviceGroupProps | null;
  onSaved: () => void; // refresh list
}

const DeviceGroupEditionModal = ({
  isOpen,
  onClose,
  groupToEdit,
  onSaved,
}: DeviceGroupModalProps) => {
  const { showSnackbar } = useContext(AppContext);

  const { mutateAsync: createGroup, isLoading: isCreating } =
    useCreateDeviceGroupAsync();
  const { mutateAsync: updateGroup, isLoading: isUpdating } =
    useUpdateDeviceGroupAsync();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DeviceGroupFormInput>({
    resolver: zodResolver(DeviceGroupFormSchema),
    defaultValues: {
      id: undefined,
      name: '',
      Devices: [],
      isActive: true,
    },
  });
  const { mutateAsync: getAllDevices } = useGetAllDevicesByLicenseAsync();

  // ✅ Snapshot das opções quando abrir (evita piscar quando a tela re-renderiza por causa do vídeo)
  const [stableDevicesOptions, setStableDevicesOptions] = useState<
    IDeviceSimpleProps[]
  >([]);
  const initializedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      initializedKeyRef.current = null;
      setStableDevicesOptions([]);
      return;
    }

    const run = async () => {
      // ✅ carrega devices só quando abre
      try {
        const res = await getAllDevices({
          pagination: { pageSize: 100, pageNumber: 1 },
        });

        const list = (res ?? []) as IDeviceSimpleProps[];
        setStableDevicesOptions(list);
      } catch (e) {
        // opcional: snackbar
      }

      // ✅ init do form só 1x por sessão
      const initKey = groupToEdit?.id ? `edit-${groupToEdit.id}` : 'new';
      if (initializedKeyRef.current === initKey) return;
      initializedKeyRef.current = initKey;

      const deviceIds = Array.isArray(groupToEdit?.devices)
        ? groupToEdit!.devices
            .map((d: any) => (typeof d === 'number' ? d : d?.id))
            .filter(Boolean)
        : [];

      if (groupToEdit?.id) {
        reset({
          id: groupToEdit.id,
          name: groupToEdit.name ?? '',
          Devices: deviceIds as number[],
        });
      } else {
        reset({
          id: undefined,
          name: '',
          Devices: [],
        });
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = handleSubmit(async (data) => {
    try {
      // ⚠️ Schema usa "Devices", mas o backend geralmente espera "devices" (camelCase).
      const payload = {
        name: data.name,
        devices: data.Devices,
        isActive: true,
      };

      if (groupToEdit?.id) {
        await updateGroup({ id: groupToEdit.id, data: payload as any });
        showSnackbar('Grupo atualizado com sucesso.', 'success');
      } else {
        await createGroup({ data: payload as any });
        showSnackbar('Grupo criado com sucesso.', 'success');
      }

      onSaved();
      onClose();
    } catch (e: any) {
      showSnackbar(e?.message ?? 'Erro ao salvar grupo.', 'error');
    }
  });

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={
        groupToEdit?.id
          ? 'Editar grupo de dispositivos'
          : 'Novo grupo de dispositivos'
      }
    >
      <Grid2 container spacing={2} sx={{ mt: 1 }}>
        <Grid2 size={{ xs: 12 }}>
          <Controller
            name="name"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                label="Nome do grupo"
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
          />
        </Grid2>

        <Grid2 size={{ xs: 12 }}>
          <Controller
            name="Devices"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropdownSelectComponent
                label={t('deviceGroupPage.devices') || 'Dispositivos do grupo'}
                options={stableDevicesOptions}
                labelField="name"
                valueField="id"
                errorMessage={error?.message}
                selectedValues={value}
                onSelectedValues={onChange}
              />
            )}
          />
        </Grid2>

        <Grid2 size={{ xs: 12 }}>
          <LoadingButton
            variant="contained"
            fullWidth
            loading={isCreating || isUpdating}
            onClick={handleSave}
          >
            Salvar
          </LoadingButton>
        </Grid2>

        {!!Object.keys(errors).length && (
          <Grid2 size={{ xs: 12 }}>
            <Typography variant="caption" color="error">
              {t('common.fixFormErrors') || 'Revise os campos obrigatórios.'}
            </Typography>
          </Grid2>
        )}
      </Grid2>
    </ModalComponent>
  );
};

/* ---------------------------
   PANEL: List / Actions
---------------------------- */
interface DeviceGroupPanelProps {
  devicesOptions: IDeviceSimpleProps[];
}

interface DeviceGroupPanelProps {
  devicesOptions: IDeviceSimpleProps[];
}

const DeviceGroupPanel = ({ devicesOptions }: DeviceGroupPanelProps) => {
  const { showSnackbar } = useContext(AppContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [groups, setGroups] = useState<IDeviceGroupProps[]>([]);
  const [groupToEdit, setGroupToEdit] = useState<IDeviceGroupProps | null>(
    null,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ✅ expandidos
  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});
  // ✅ cadeado por device (UI local)
  const [lockedDevices, setLockedDevices] = useState<Record<string, boolean>>(
    {},
  );
  const [isEventsModalOpen, setIsEventsModalOpen] = useState(false);
  const [eventsDeviceId, setEventsDeviceId] = useState<number | null>(null);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);

  const handleOpenDoor = async (deviceId: number) => {
    try {
      await mutateOpenDoor(deviceId);

      showSnackbar('Porta acionada com sucesso.', 'success');

      setSelectedDeviceId(deviceId);
      setIsRegisterModalOpen(true); // 🔥 abre modal
    } catch (e: any) {
      showSnackbar(e?.message ?? 'Erro ao acionar porta.', 'error');
    }
  };

  const { mutateAsync: getAllGroups, isLoading: isLoadingGroups } =
    useGetAllDeviceGroupByLicenseAsync();

  const { mutateAsync: getGroupById } = useGetDeviceGroupByIdAsync();

  const { mutateAsync: deleteGroup, isLoading: isDeleting } =
    useDeleteDeviceGroupAsync();

  const loadGroups = async () => {
    try {
      setIsRefreshing(true);
      const res = await getAllGroups();
      setGroups((res ?? []) as IDeviceGroupProps[]);
    } catch (e: any) {
      showSnackbar(e?.message ?? 'Erro ao carregar grupos.', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };
  const { mutateAsync: mutateOpenDoor, isLoading: isOpeningDoor } =
    useOpenDoorAsync();

  useEffect(() => {
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    setGroupToEdit(null);
    setIsModalOpen(true);
  };

  const handleEdit = async (id: number) => {
    try {
      const full = await getGroupById(id);
      setGroupToEdit(full as IDeviceGroupProps);
      setIsModalOpen(true);
    } catch (e: any) {
      showSnackbar(e?.message ?? 'Erro ao buscar grupo.', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    const ok = window.confirm('Deseja realmente excluir este grupo?');
    if (!ok) return;

    try {
      await deleteGroup(id);
      showSnackbar('Grupo excluído com sucesso.', 'success');
      await loadGroups();
    } catch (e: any) {
      showSnackbar(e?.message ?? 'Erro ao excluir grupo.', 'error');
    }
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setGroupToEdit(null);
  };

  const toggleGroupOpen = (groupId: number) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const toggleDeviceLock = (groupId: number, deviceId: number) => {
    const key = `${groupId}-${deviceId}`;
    setLockedDevices((prev) => {
      const next = !prev[key];
      // snackbar opcional
      showSnackbar(
        next ? 'Dispositivo bloqueado (UI)' : 'Dispositivo desbloqueado (UI)',
        'info',
      );
      return { ...prev, [key]: next };
    });

    // 🔥 Se quiser persistir depois:
    // chama mutation aqui (ex: updateDeviceGroupAsync ou endpoint específico)
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        p: 2,
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t('deviceGroupPage.title') || 'Grupos de dispositivos'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('common.refresh') || 'Atualizar'}>
            <span>
              <IconButton
                size="small"
                onClick={loadGroups}
                disabled={isLoadingGroups || isRefreshing}
                color="inherit"
              >
                <RefreshIcon fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>

          <LoadingButton
            variant="contained"
            size="small"
            onClick={handleCreate}
            disabled={devicesOptions.length === 0}
          >
            {t('deviceGroupPage.new') || 'Novo grupo'}
          </LoadingButton>
          <Tooltip title="Histórico de aberturas">
            <IconButton
              size="small"
              color="inherit"
              onClick={() => {
                setEventsDeviceId(null); // ou um device específico
                setIsEventsModalOpen(true);
              }}
            >
              <HistoryIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ opacity: 0.7, mb: 1 }}>
        {t('deviceGroupPage.hint') ||
          'Crie grupos para organizar dispositivos.'}
      </Typography>

      <Divider sx={{ mb: 1 }} />

      {isLoadingGroups ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={26} />
        </Box>
      ) : groups.length === 0 ? (
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {t('deviceGroupPage.empty') || 'Nenhum grupo cadastrado.'}
        </Typography>
      ) : (
        <List
          dense
          disablePadding
          sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
        >
          {groups.map((g) => {
            const isOpen = !!openGroups[g.id];
            const total = g.devices?.length ?? 0;
            const online = (g.devices ?? []).filter((d) => d.isOnline).length;

            return (
              <Box
                key={g.id}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: '#202940',
                  overflow: 'hidden',
                }}
              >
                {/* CABEÇALHO DO GRUPO */}
                <ListItem
                  disableGutters
                  sx={{ px: 1 }}
                  secondaryAction={
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Tooltip title={t('common.edit') || 'Editar'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(g.id)}
                            color="primary"
                          >
                            <EditIcon fontSize="medium" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title={t('common.delete') || 'Excluir'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(g.id)}
                            disabled={isDeleting}
                            color="error"
                          >
                            <DeleteIcon fontSize="medium" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemButton
                    onClick={() => toggleGroupOpen(g.id)}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      px: 1,
                      mr: 6, // espaço pro secondaryAction
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      {isOpen ? (
                        <KeyboardArrowDown fontSize="medium" />
                      ) : (
                        <KeyboardArrowUp fontSize="medium" />
                      )}

                      <ListItemText
                        primary={g.name}
                        primaryTypographyProps={{ fontWeight: 800 }}
                        secondary={`${online}/${total} ${t('monitoringPage.online') || 'Online'}`}
                      />

                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          g.isActive
                            ? t('common.active') || 'Ativo'
                            : t('common.inactive') || 'Inativo'
                        }
                        sx={{ ml: 'auto' }}
                      />
                    </Box>
                  </ListItemButton>
                </ListItem>

                {/* LISTA DE DEVICES (EXPAND) */}
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 2, pb: 1.5 }}>
                    <Divider sx={{ mb: 1 }} />

                    <Typography
                      variant="subtitle2"
                      fontWeight={800}
                      sx={{ mb: 1 }}
                    >
                      {t('deviceGroupPage.devices') || 'Dispositivos no grupo'}
                    </Typography>

                    <List
                      dense
                      disablePadding
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      {(g.devices ?? []).map((d) => {
                        const key = `${g.id}-${d.id}`;
                        const isLocked = !!lockedDevices[key];

                        return (
                          <Box
                            key={d.id}
                            sx={{
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: 'divider',
                              backgroundColor: 'null',
                              px: 1,
                              py: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {/* ✅ seta lateral do device */}
                              <CameraIcon />

                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={800}
                                  noWrap
                                >
                                  {d.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ opacity: 0.8 }}
                                  noWrap
                                >
                                  {d.isOnline
                                    ? t('monitoringPage.online') || 'Online'
                                    : t('monitoringPage.offline') || 'Offline'}
                                </Typography>
                              </Box>

                              {/* ✅ ação com cadeado */}
                              <Tooltip
                                title={
                                  !d.isOnline
                                    ? t('monitoringPage.deviceOffline') ||
                                      'Dispositivo offline'
                                    : t('deviceGroupPage.open') || 'Abrir'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleOpenDoor(d.id)}
                                    disabled={!d.isOnline}
                                  >
                                    <LockOpenOutlined fontSize="medium" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </Box>
                        );
                      })}

                      {(g.devices?.length ?? 0) === 0 && (
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                          Nenhum dispositivo neste grupo.
                        </Typography>
                      )}
                    </List>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </List>
      )}

      <DeviceGroupEditionModal
        isOpen={isModalOpen}
        onClose={onCloseModal}
        devicesOptions={devicesOptions}
        groupToEdit={groupToEdit}
        onSaved={loadGroups}
      />
      <OpenDoorEventsModal
        open={isEventsModalOpen}
        deviceId={eventsDeviceId}
        onClose={() => {
          setIsEventsModalOpen(false);
          setEventsDeviceId(null);
        }}
      />
      <RegisterOpenDoorModal
        open={isRegisterModalOpen}
        deviceId={selectedDeviceId}
        onClose={() => {
          setIsRegisterModalOpen(false);
          setSelectedDeviceId(null);
        }}
      />
    </Box>
  );
};

/* ---------------------------
   SCREEN: Monitoring + side panel
---------------------------- */
const getTileSize = (n: number) => {
  // tamanhos responsivos pro mosaico
  if (n <= 1) return { xs: 12 };
  if (n === 2) return { xs: 12, md: 6 };
  if (n === 3) return { xs: 12, sm: 6, lg: 4 };
  if (n === 4) return { xs: 12, sm: 6, lg: 6 }; // 2x2 no lg
  if (n <= 6) return { xs: 12, sm: 6, lg: 4 }; // até 3 por linha no lg
  return { xs: 12, sm: 6, md: 4, lg: 3 }; // 4 por linha no lg
};

const DeviceMonitoringScreen = ({
  isDirectConnection = false,
  initialDeviceId,
}: DeviceMonitoringScreenProps) => {
  const [devices, setDevices] = useState<IDeviceSimpleProps[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);

  const { mutateAsync: getAllDevices } = useGetAllDevicesByLicenseAsync();

  const { control, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      deviceId: initialDeviceId ? [initialDeviceId] : [],
    },
  });

  // ✅ agora é array (multi)
  const selectedDeviceIds = watch('deviceId') ?? [];

  const selectedDevices = useMemo(() => {
    if (!selectedDeviceIds.length) return [];
    return devices.filter((d) => selectedDeviceIds.includes(d.id));
  }, [devices, selectedDeviceIds]);

  const onlineCount = useMemo(
    () => selectedDevices.filter((d) => d.isOnline).length,
    [selectedDevices],
  );

  const decodedToken = useMemo(() => {
    const token = getToken();
    return token ? atob(token) : null;
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingDevices(true);

        const res = await getAllDevices({
          pagination: { pageSize: 100, pageNumber: 1 },
        });

        const list = (res ?? []) as IDeviceSimpleProps[];
        setDevices(list);

        // seleção inicial (1 item)
        if (initialDeviceId && list.some((d) => d.id === initialDeviceId)) {
          setValue('deviceId', [initialDeviceId]);
          return;
        }

        // se não tiver nada selecionado, seleciona o primeiro
        if (
          (!selectedDeviceIds || selectedDeviceIds.length === 0) &&
          list.length > 0
        ) {
          setValue('deviceId', [list[0].id]);
        }
      } finally {
        setLoadingDevices(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderStreamForDevice = (device: IDeviceSimpleProps) => {
    if (!device?.id) return null;

    if (isDirectConnection) {
      return (
        <DeviceDirectConnectionStreamComponent
          deviceId={device.id}
          deviceName={device.name}
          isEnableVideo={device.isOnline}
        />
      );
    }

    if (!decodedToken) return null;

    return (
      <DeviceReverseConnectionStreamComponent
        deviceId={device.id}
        isEnableVideo={device.isOnline}
        token={decodedToken}
      />
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid2 container spacing={2}>
        {/* LEFT: stream */}
        <Grid2 size={{ xs: 12, lg: 8 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Top bar */}
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={{ xs: 12, md: 8 }}>
                <Controller
                  name="deviceId"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <DropdownSelectComponent
                      label={
                        t('monitoringPage.selectDevice') ||
                        'Selecionar dispositivo'
                      }
                      options={devices}
                      labelField="name"
                      valueField="id"
                      errorMessage={error?.message}
                      selectedValues={value}
                      onSelectedValues={onChange}
                      disabled={loadingDevices}
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 4 }}>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('monitoringPage.selected') || 'Selecionados'}:{' '}
                    {selectedDevices.length}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                    {t('monitoringPage.online') || 'Online'}: {onlineCount} •{' '}
                    {t('monitoringPage.offline') || 'Offline'}:{' '}
                    {Math.max(0, selectedDevices.length - onlineCount)}
                  </Typography>
                </Box>
              </Grid2>
            </Grid2>

            {/* Video / Mosaic */}
            <Box
              sx={{
                width: '100%',
                minHeight: 700,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#0b0b0b',
                p: 1, // padding pro mosaico
              }}
            >
              {selectedDevices.length > 0 ? (
                <Grid2 container spacing={1}>
                  {selectedDevices.map((device) => (
                    <Grid2
                      key={device.id}
                      size={getTileSize(selectedDevices.length)}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: selectedDevices.length === 1 ? 700 : 340, // mosaico: tiles menores
                          borderRadius: 2,
                          overflow: 'hidden',
                          bgcolor: '#000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {renderStreamForDevice(device)}

                        {/* overlay com nome + status */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 8,
                            bottom: 8,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            maxWidth: '90%',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700 }}
                          >
                            {device.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', opacity: 0.85 }}
                          >
                            {device.isOnline
                              ? t('monitoringPage.online') || 'Online'
                              : t('monitoringPage.offline') || 'Offline'}
                          </Typography>
                        </Box>

                        {/* overlay se offline */}
                        {!device.isOnline && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              bgcolor: 'rgba(0,0,0,0.45)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography sx={{ color: '#fff', opacity: 0.9 }}>
                              {t('monitoringPage.deviceOffline') ||
                                'Dispositivo offline'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid2>
                  ))}
                </Grid2>
              ) : !loadingDevices ? (
                <Box
                  sx={{
                    height: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ color: 'common.white', opacity: 0.8 }}>
                    {t('monitoringPage.selectDeviceHint') ||
                      'Selecione um dispositivo para visualizar o vídeo.'}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ color: 'common.white', opacity: 0.8 }}>
                    {t('monitoringPage.loadingDevices') ||
                      'Carregando dispositivos...'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Grid2>

        {/* RIGHT: groups */}
        <Grid2 size={{ xs: 12, lg: 4 }}>
          {/* mantém o seu painel como está */}
          <DeviceGroupPanel devicesOptions={devices} />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default DeviceMonitoringScreen;
